import { darken, lighten } from "polished";
import { white } from "../colors";
import { brandPrimary, brandSecondary } from "../colors/brand";
import * as steelGrey from "../colors/steelGrey";
import * as deepOrange from "../colors/deepOrange";
import * as deepBlue from "../colors/deepBlue";
import * as red from "../colors/red";
import * as yellow from "../colors/yellow";
import * as lightGreen from "../colors/lightGreen";
import * as green from "../colors/green";
import * as lime from "../colors/lime";
import * as amber from "../colors/amber";
import * as aqua from "../colors/aqua";
import * as blue from "../colors/blue";
import * as cyan from "../colors/cyan";
import * as teal from "../colors/teal";
import * as orange from "../colors/orange";
import * as pink from "../colors/pink";
import * as lightBlue from "../colors/lightBlue";
import * as lightPurple from "../colors/lightPurple";
import * as deepPink from "../colors/deepPink";
import * as purple from "../colors/purple";
import * as deepPurple from "../colors/deepPurple";
import { visualizationDefaultLight } from "../visualization-palettes/default";
export const alchemyLight = {
    // Base (structural)
    structural: {
        base: steelGrey.steelGrey400,
        inverse: white,
        contrast: steelGrey.steelGrey1000,
        highlight: steelGrey.steelGrey100,
        hint: steelGrey.steelGrey50,
        // Brand colors
        brandPrimary,
        brandSecondary,
        // Text colors
        typeLink: "#266dc9", // todo: see if we can use darken for original color deepBlue.deepBlue500
        typeHeader: "#1b2437", // todo: see if we can use darken for original color steelGrey.steelGrey900
        typeSubHeader: "#6B7588",
        typeBody: "#1b2437", // todo: see if we can use darken for original color steelGrey.steelGrey900
        typeMeta: "#6B7588",
        // UI
        line: lighten(0.05, steelGrey.steelGrey200),
        lineContrast: steelGrey.steelGrey200,
        inputBorder: lighten(0.02, steelGrey.steelGrey300),
        fill: steelGrey.steelGrey100,
        canvas: steelGrey.steelGrey50,
        container: white
    },
    // Interactive
    interactiveNavigational: {
        contrast: steelGrey.steelGrey600,
        base: steelGrey.steelGrey500,
        highlight: steelGrey.steelGrey200,
        hint: steelGrey.steelGrey50
    },
    interactiveConstructive: {
        contrast: deepBlue.deepBlue800,
        base: deepBlue.deepBlue500,
        highlight: deepBlue.deepBlue200,
        hint: deepBlue.deepBlue50
    },
    interactiveDestructive: {
        contrast: red.redA500,
        base: red.redA400,
        highlight: red.red200,
        hint: red.red50
    },
    interactiveAction: {
        contrast: darken(0.02, green.green1000),
        base: green.greenA500,
        highlight: green.green200,
        hint: green.green50
    },
    // Expressive
    expressivePositive: {
        contrast: green.greenA1000,
        base: green.greenA400,
        a11yBase: darken(0.091, green.greenA400),
        gradient: green.green600,
        highlight: green.green200,
        hint: green.green50
    },
    expressiveSemiPositive: {
        contrast: lime.limeA1000,
        base: lime.limeA400,
        a11yBase: lime.limeA1000,
        gradient: lime.lime500,
        highlight: lime.lime200,
        hint: lime.lime50
    },
    expressiveNegative: {
        contrast: red.redA1000,
        base: red.redA400,
        a11yBase: lighten(0.047, red.redA400),
        gradient: red.red500,
        highlight: red.red200,
        hint: red.red50
    },
    expressiveSemiNegative: {
        contrast: deepOrange.deepOrangeA1000,
        base: deepOrange.deepOrangeA400,
        a11yBase: darken(0.022, deepOrange.deepOrange1000),
        gradient: deepOrange.deepOrange400,
        highlight: deepOrange.deepOrange200,
        hint: deepOrange.deepOrange50
    },
    expressiveWarning: {
        contrast: yellow.yellowA1000,
        base: yellow.yellowA400,
        a11yBase: lighten(0.11, yellow.yellowA1000),
        gradient: yellow.yellow500,
        highlight: yellow.yellow200,
        hint: yellow.yellow50
    },
    expressiveInformative: {
        contrast: "#102A49", // shade(0.64, deepBlue.deepBlue500) gets us close but not exact. Hardcode for now until we overhaul theming.
        base: deepBlue.deepBlue500,
        a11yBase: deepBlue.deepBlue500,
        gradient: deepBlue.deepBlue400,
        highlight: deepBlue.deepBlue200,
        hint: deepBlue.deepBlue50
    },
    expressiveNeutral: {
        contrast: steelGrey.steelGrey1000,
        gradient: steelGrey.steelGreyA200,
        a11yBase: steelGrey.steelGrey500,
        base: steelGrey.steelGrey300,
        highlight: steelGrey.steelGrey200,
        hint: steelGrey.steelGrey50
    },
    // Illustrative
    illustrativePurple: {
        a11yBase: purple.purple600,
        base: purple.purple500,
        contrast: purple.purple800,
        highlight: purple.purple200,
        hint: purple.purple50
    },
    illustrativeBlue: {
        a11yBase: blue.blue700,
        base: blue.blue500,
        contrast: blue.blue800,
        highlight: blue.blue200,
        hint: blue.blue50
    },
    illustrativeLightBlue: {
        a11yBase: lightBlue.lightBlue800,
        base: lightBlue.lightBlue500,
        contrast: lightBlue.lightBlue900,
        highlight: lightBlue.lightBlue200,
        hint: lightBlue.lightBlue50
    },
    illustrativeCyan: {
        a11yBase: cyan.cyan800,
        base: cyan.cyan500,
        contrast: cyan.cyan900,
        highlight: cyan.cyan200,
        hint: cyan.cyan50
    },
    illustrativeAqua: {
        a11yBase: darken(0.11, aqua.aqua900),
        base: aqua.aqua500,
        contrast: aqua.aqua1000,
        highlight: aqua.aqua200,
        hint: aqua.aqua50
    },
    illustrativeTeal: {
        a11yBase: darken(0.017, teal.teal900),
        base: teal.teal500,
        contrast: teal.teal900,
        highlight: teal.teal200,
        hint: teal.teal50
    },
    illustrativeGreen: {
        a11yBase: green.green1000,
        base: green.green500,
        contrast: green.green900,
        highlight: green.green200,
        hint: green.green50
    },
    illustrativeLightGreen: {
        a11yBase: darken(0.091, lightGreen.lightGreen900),
        base: lightGreen.lightGreen500,
        contrast: lightGreen.lightGreen900,
        highlight: lightGreen.lightGreen200,
        hint: lightGreen.lightGreen50
    },
    illustrativeLime: {
        a11yBase: darken(0.061, lime.lime1000),
        base: lime.lime500,
        contrast: lime.lime1000,
        highlight: lime.lime200,
        hint: lime.lime50
    },
    illustrativeYellow: {
        a11yBase: darken(0.36, yellow.yellow500),
        base: yellow.yellow500,
        contrast: yellow.yellowA500,
        highlight: yellow.yellow200,
        hint: yellow.yellow50
    },
    illustrativeOrange: {
        a11yBase: darken(0.17, orange.orangeA400),
        base: orange.orange500,
        contrast: orange.orangeA400,
        highlight: orange.orange200,
        hint: orange.orange50
    },
    illustrativeAmber: {
        a11yBase: amber.amber900,
        base: amber.amber500,
        contrast: amber.amber700,
        highlight: amber.amber200,
        hint: amber.amber50
    },
    illustrativeDeepOrange: {
        a11yBase: deepOrange.deepOrange800,
        base: deepOrange.deepOrange500,
        contrast: deepOrange.deepOrange700,
        highlight: deepOrange.deepOrange200,
        hint: deepOrange.deepOrange50
    },
    illustrativeRed: {
        a11yBase: red.red700,
        base: red.red500,
        contrast: red.red600,
        highlight: red.red200,
        hint: red.red50
    },
    illustrativePink: {
        a11yBase: pink.pink900,
        contrast: pink.pink700,
        base: pink.pink500,
        highlight: pink.pink200,
        hint: pink.pink50
    },
    illustrativeDeepPink: {
        a11yBase: deepPink.deepPink800,
        contrast: deepPink.deepPink900,
        base: deepPink.deepPink500,
        highlight: deepPink.deepPink200,
        hint: deepPink.deepPink50
    },
    illustrativeLightPurple: {
        a11yBase: lightPurple.lightPurple700,
        base: lightPurple.lightPurple500,
        contrast: lightPurple.lightPurple800,
        highlight: lightPurple.lightPurple200,
        hint: lightPurple.lightPurple50
    },
    illustrativeDeepPurple: {
        a11yBase: deepPurple.deepPurple600,
        base: deepPurple.deepPurple500,
        contrast: deepPurple.deepPurple800,
        highlight: deepPurple.deepPurple200,
        hint: deepPurple.deepPurple50
    },
    illustrativeDeepBlue: {
        a11yBase: deepBlue.deepBlue600,
        base: deepBlue.deepBlue500,
        contrast: deepBlue.deepBlue800,
        highlight: deepBlue.deepBlue100,
        hint: deepBlue.deepBlue50
    },
    illustrativeSteelGrey: {
        a11yBase: steelGrey.steelGrey700,
        base: steelGrey.steelGrey300,
        contrast: steelGrey.steelGrey900,
        highlight: steelGrey.steelGrey200,
        hint: steelGrey.steelGrey50
    },
    illustrativeBrand: {
        a11yBase: darken(0.02, purple.purple500),
        base: brandPrimary,
        contrast: darken(0.2, purple.purple500),
        highlight: lighten(0.25, purple.purple500),
        hint: lighten(0.34, purple.purple500)
    },
    // Sentiment
    sentimentInformative: {
        contrast: deepBlue.deepBlueA1000,
        base: deepBlue.deepBlue500,
        highlight: deepBlue.deepBlue300,
        hint: deepBlue.deepBlue50
    },
    sentimentNegative: {
        contrast: red.redA1000,
        base: red.redA400,
        highlight: red.red300,
        hint: red.red50
    },
    sentimentNeutral: {
        contrast: steelGrey.steelGrey1000,
        base: steelGrey.steelGrey300,
        highlight: lighten(0.169, steelGrey.steelGrey300),
        hint: steelGrey.steelGrey50
    },
    sentimentPositive: {
        contrast: green.greenA1000,
        base: green.greenA400,
        highlight: green.green300,
        hint: green.green50
    },
    sentimentSemiNegative: {
        contrast: deepOrange.deepOrangeA1000,
        base: deepOrange.deepOrange500,
        highlight: deepOrange.deepOrange300,
        hint: deepOrange.deepOrange50
    },
    sentimentSemiPositive: {
        contrast: lime.limeA1000,
        base: lime.limeA400,
        highlight: lime.lime300,
        hint: lime.lime50
    },
    sentimentWarning: {
        contrast: yellow.yellowA1000,
        base: yellow.yellowA400,
        highlight: yellow.yellow300,
        hint: yellow.yellow50
    },
    ...visualizationDefaultLight
};
